
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { ComplianceCard as IComplianceCard, CoOwnerEntry, ComplianceSectionA, ComplianceSectionB, ApprovalHistoryEntry } from "../../types/compliance";
import { FileUploadKit } from "../common/FileUploadKit";
import { useAuth } from "../../context/AuthContext";
import { isSuperUser, getDeptCodeByEmail } from "../../config/roles";
import { DEPT_CODE_TO_NAME_TW, getDeptNameTW, getDeptNameByCode } from "../../constants/complianceMappingTW";
import { updateComplianceCard, submitCardForApproval } from "../../services/complianceService";
import { uploadGovProjectFiles } from "../../services/storageUpload"; 
import { useToast } from "../Toast";
import { ApprovalFlowPanel } from "../approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../../hooks/useApprovalFlow";
import { updateStageDecision } from "../../services/approvalFlowService";
import { formatDate } from "../../utils/dateUtils";
import { stripUndefinedDeep } from "../../utils/firestoreClean"; 
import { Modal } from "../Modal";
import { DEPARTMENTS } from "../../constants/departments";

interface Props {
  card: IComplianceCard;
  onRefresh: () => void;
  readOnly?: boolean;
}

// Auto-resize Textarea Component
const AutoResizeTextarea = ({ value, disabled, placeholder, className, style }: any) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useLayoutEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value]);
    return (
        <textarea
            ref={textareaRef}
            className={className}
            style={{ ...style, resize: 'none', overflow: 'hidden' }}
            value={value || ""}
            readOnly={true} 
            disabled={disabled}
            placeholder={placeholder}
        />
    );
};

// Add Co-Owner Modal
function AddCoOwnerModal({ onClose, onConfirm }: { onClose: () => void, onConfirm: (code: string, name: string) => void }) {
    const [selectedDept, setSelectedDept] = useState("");
    return (
        <Modal title="新增協辦單位" onClose={onClose} width={400}>
            <div className="field">
                <label className="label">選擇部門</label>
                <select className="input" value={selectedDept} onChange={e => setSelectedDept(e.target.value)}>
                    <option value="">請選擇...</option>
                    {DEPARTMENTS.map(d => (
                        <option key={d.id} value={d.code}>{d.name} ({d.code})</option>
                    ))}
                </select>
            </div>
            <div className="row" style={{justifyContent: 'flex-end', marginTop: 20}}>
                <button className="btn" style={{width:'auto'}} onClick={() => {
                    if (!selectedDept) return alert("請選擇部門");
                    const name = DEPARTMENTS.find(d => d.code === selectedDept)?.name || selectedDept;
                    onConfirm(selectedDept, name);
                }}>
                    確認新增
                </button>
            </div>
        </Modal>
    );
}

export const ComplianceCard: React.FC<Props> = ({ card, onRefresh, readOnly }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isSuper = isSuperUser(user?.email);
  const userDeptCode = getDeptCodeByEmail(user?.email);
  const userDeptName = getDeptNameTW(userDeptCode);
  const canManageCoop = isSuper || userDeptCode === 'LG';

  const [formData, setFormData] = useState<IComplianceCard>({ ...card });
  const [saving, setSaving] = useState(false);
  const [showAssistModal, setShowAssistModal] = useState(false);

  // Flow Hook (Current Engine)
  const { flow, loading: flowLoading } = useApprovalFlow("COMPLIANCE_PROJECT", card.id);
  
  useEffect(() => { setFormData({ ...card }); }, [card]);

  const secA: ComplianceSectionA = formData.sectionA || { title: "", attachments: [], summary: "", publishDate: "", regulatorName: "", announcementUrl: "", sourceUrl: "" };
  const secB: ComplianceSectionB = formData.sectionB || { attachments: [], ownerDeptName: "", ownerDeptCode: "", ownerAssigneeName: "", ownerContent: "" };
  const secC = formData.sectionC || { coowners: [] };
  if (!secC.coowners || secC.coowners.length === 0) {
      secC.coowners = [{ deptCode: "LG", deptName: "法務部", attachments: [], comment: "" }];
  }

  // --- Submission (Standard) ---
  const handleSubmitFlow = async () => {
      if (!confirm("確定送出呈核？")) return;
      setSaving(true);
      try {
          await submitCardForApproval(card, user?.email||'unknown', user?.id||'unknown', user?.displayName||'User');
          showToast("已送出");
          onRefresh();
      } catch(e: any) {
          showToast(`送出失敗: ${e.message}`, "error");
      } finally {
          setSaving(false);
      }
  };

  const handleSave = async (silent = false) => {
    setSaving(true);
    try {
        const cleanData = stripUndefinedDeep(formData);
        await updateComplianceCard(card.id, cleanData);
        if (!silent) showToast("儲存成功");
        onRefresh();
    } catch(e: any) {
        showToast("儲存失敗: " + e.message, "error");
    } finally {
        setSaving(false);
    }
  };

  const handleAddCoOwner = (code: string, name: string) => {
      const newEntry: CoOwnerEntry = { deptCode: code, deptName: name, assigneeName: "", comment: "", attachments: [] };
      setFormData(prev => ({ ...prev, sectionC: { coowners: [...secC.coowners, newEntry] } }));
      setShowAssistModal(false);
  };

  const handleUpload = async (files: File[], target: 'B'|'C', coOwnerIndex?: number) => {
      try {
          const uploaded = await uploadGovProjectFiles(card.id, files);
          const newAtts = uploaded.map(u => ({ name: u.name, url: u.url, size: u.size, contentType: u.contentType }));
          if (target === 'B') {
              setFormData(p => ({...p, sectionB: {...(p.sectionB || secB), attachments: [...(p.sectionB?.attachments || []), ...newAtts]}}));
          } else if (target === 'C' && typeof coOwnerIndex === 'number') {
              const coowners = [...secC.coowners];
              coowners[coOwnerIndex].attachments = [...coowners[coOwnerIndex].attachments, ...newAtts];
              setFormData(p => ({...p, sectionC: { coowners }}));
          }
      } catch(e) { console.error(e); showToast("上傳失敗", "error"); }
  };

  const handleDecision = async (stageKey: string, decision: any, comment: string) => {
      if (!flow) return;
      setSaving(true);
      try {
          await updateStageDecision({ flowId: flow.id, stageKey: stageKey as any, decision, comment, actorUid: user?.id||'', actorName: user?.displayName||'User' });
          
          let newStatus = formData.status;
          if (decision === 'REJECTED') newStatus = 'REJECTED';
          else if (decision === 'RETURNED') newStatus = 'RETURNED';
          else if (decision === 'APPROVED' && stageKey === 'MANAGER_IRENE') newStatus = 'APPROVED';
          else if (decision === 'APPROVED' && stageKey === 'LEGAL_REVIEW') newStatus = 'IN_REVIEW';

          await updateComplianceCard(card.id, { status: newStatus });
          showToast(`操作成功: ${decision}`);
          onRefresh();
      } catch(e: any) {
          showToast(`失敗: ${e.message}`, "error");
      } finally {
          setSaving(false);
      }
  };

  const isOwner = isSuper || (userDeptCode === secB.ownerDeptCode) || (userDeptName === secB.ownerDeptName);
  const canEditPrimary = isOwner && !['APPROVED', 'ARCHIVED', 'COMPLETED', 'REJECTED'].includes(formData.status);
  
  const effectiveStatus = formData.status;
  const isTerminal = ['APPROVED', 'REJECTED', 'CANCELLED'].includes(effectiveStatus);

  return (
    <div style={{paddingBottom: 40}}>
        {/* Header */}
        <div style={{marginBottom: 16, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div style={{display:'flex', gap:10}}>
                <span className="badge" style={{fontSize:14, borderColor: '#ccc', color: '#ccc'}}>
                    {effectiveStatus} (v{formData.revision})
                </span>
            </div>
        </div>

        {/* SECTION A: Source Information (Restored & Read-Only) */}
        <div className="section" style={{background: '#1a1d21', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 20}}>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:16}}>
                <h4 style={{color:'#93c5fd', margin:0}}>A. 來源資訊 (Source)</h4>
                <span className="badge" style={{background:'#333', color:'#aaa', fontSize:10}}>唯讀 (System)</span>
            </div>
            
            <div className="grid" style={{gridTemplateColumns: '2fr 1fr', gap: 16}}>
                <div style={{gridColumn: '1 / -1'}}>
                    <div style={{fontSize: 12, color: '#aaa', marginBottom: 4}}>公告標題</div>
                    <div className="p" style={{color:'#fff', fontWeight:'bold', padding:'8px', background:'rgba(255,255,255,0.05)', borderRadius:6}}>
                        {secA.title || "(無標題)"}
                    </div>
                </div>
                
                <div>
                    <div style={{fontSize: 12, color: '#aaa', marginBottom: 4}}>主管機關</div>
                    <div className="input" style={{background:'#222', color:'#ddd', border:'1px solid #444'}}>
                        {secA.regulatorName || "未知"}
                    </div>
                </div>
                <div>
                    <div style={{fontSize: 12, color: '#aaa', marginBottom: 4}}>發布日期</div>
                    <div className="input" style={{background:'#222', color:'#ddd', border:'1px solid #444'}}>
                        {secA.publishDate ? formatDate(new Date(secA.publishDate)) : "—"}
                    </div>
                </div>

                <div style={{gridColumn: '1 / -1'}}>
                    <div style={{fontSize: 12, color: '#aaa', marginBottom: 4}}>公告連結</div>
                    {secA.sourceUrl || secA.announcementUrl ? (
                         <a href={secA.sourceUrl || secA.announcementUrl || "#"} target="_blank" rel="noreferrer" style={{color:'#60a5fa', wordBreak:'break-all'}}>
                             {secA.sourceUrl || secA.announcementUrl}
                         </a>
                    ) : <span className="p">—</span>}
                </div>

                <div style={{gridColumn: '1 / -1'}}>
                    <div style={{fontSize: 12, color: '#aaa', marginBottom: 4}}>內容摘要</div>
                    <AutoResizeTextarea 
                        className="input" 
                        value={secA.summary} 
                        disabled 
                        style={{background:'#222', color:'#ccc', border:'1px solid #444', minHeight: 80}} 
                    />
                </div>
                
                {/* Attachments for A */}
                <div style={{gridColumn: '1 / -1'}}>
                    <div style={{fontSize: 12, color: '#aaa', marginBottom: 4}}>來源附件</div>
                    <div style={{display:'flex', flexWrap:'wrap', gap: 10}}>
                        {secA.attachments && secA.attachments.length > 0 ? 
                             secA.attachments.map((a, i) => (
                                 <a key={i} href={a.url} target="_blank" rel="noreferrer" className="badge" style={{textDecoration:'none', color:'#60a5fa', borderColor:'#444'}}>
                                     📄 {a.name}
                                 </a>
                             )) 
                             : <span className="p" style={{color:'#666'}}>無附件</span>
                        }
                    </div>
                </div>
            </div>
        </div>

        <div className="section" style={{background: '#222', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 20}}>
            <h4 style={{color:'#fdba74', marginBottom:16}}>B. 內部擬辦 (Owner)</h4>
            <textarea className="input" rows={3} value={secB.ownerContent || ""} onChange={e=>setFormData({...formData, sectionB: {...secB, ownerContent: e.target.value}})} disabled={!canEditPrimary} />
            <div style={{marginTop:8}}>
                <FileUploadKit existingFiles={secB.attachments} disabled={!canEditPrimary} onFilesSelected={files => handleUpload(files, 'B')} />
            </div>
        </div>

        {/* SECTION C: Co-Assist */}
        <div className="section" style={{background: '#222', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 20}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
                <h4 style={{color:'#34d399', margin:0}}>C. 協辦單位 (Co-Assist)</h4>
                {canManageCoop && !isTerminal && (
                    <button className="smallbtn" onClick={() => setShowAssistModal(true)}>+ 新增協辦</button>
                )}
            </div>
            
            {secC.coowners.map((co, idx) => {
                const isCoOwner = isSuper || userDeptCode === co.deptCode;
                const canEditThis = isCoOwner && !isTerminal;

                return (
                    <div key={idx} style={{marginBottom: 16, padding: 12, background:'rgba(255,255,255,0.03)', borderRadius: 6, borderLeft: '3px solid #34d399'}}>
                        <div style={{fontWeight:'bold', marginBottom: 8, color: '#fff'}}>{co.deptName} ({co.deptCode})</div>
                        <textarea 
                            className="input" 
                            rows={2} 
                            placeholder="協辦回覆 / 意見..." 
                            value={co.comment || ""} 
                            onChange={e => {
                                const newCoowners = [...secC.coowners];
                                newCoowners[idx] = { ...co, comment: e.target.value };
                                setFormData(p => ({...p, sectionC: { coowners: newCoowners }}));
                            }}
                            disabled={!canEditThis}
                        />
                        <div style={{marginTop: 8}}>
                             <FileUploadKit 
                                 existingFiles={co.attachments} 
                                 disabled={!canEditThis} 
                                 onFilesSelected={files => handleUpload(files, 'C', idx)} 
                             />
                        </div>
                    </div>
                );
            })}
        </div>

        {/* SECTION D: Flow */}
        <div className="section" style={{background: '#1a1d21', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 20}}>
            <h4 style={{color:'#a78bfa', borderBottom:'1px solid #444', paddingBottom:8, marginTop:0}}>D. 簽核狀態</h4>
            
            {flowLoading ? <div>Loading...</div> : (
                flow ? <ApprovalFlowPanel flow={flow} onDecision={handleDecision} /> : (
                    <div className="p" style={{color:'#888'}}>
                        {['DRAFT', 'RETURNED'].includes(formData.status) ? "尚未啟動簽核流程" : "無進行中流程"}
                    </div>
                )
            )}
        </div>
        
        {/* Actions */}
        {canEditPrimary && !isTerminal && (
            <div style={{marginTop: 30, padding: 20, background: '#111', borderRadius: 12, border: '1px solid #333', display: 'flex', justifyContent: 'flex-end', gap: 12}}>
                <button className="btn" style={{width:'auto', background:'#333'}} onClick={() => handleSave(false)} disabled={saving}>暫存草稿</button>
                
                {/* Standard Submit Button */}
                <button className="btn" style={{width:'auto', background:'#3b82f6', color:'#fff'}} onClick={handleSubmitFlow} disabled={saving}>
                    送出呈核
                </button>
            </div>
        )}

        {showAssistModal && <AddCoOwnerModal onClose={()=>setShowAssistModal(false)} onConfirm={handleAddCoOwner} />}
    </div>
  );
};
